import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DoMenuScenarioTab {
  scenarioId: number;
  scenarioName: string;
  icon: string;
  daysheetMenuItemsList: DoMenuItem[];
}

export interface DoMenuItem {
  daysheetMenuItemId: number;
  daysheetMenuItemName: string;
}

@Component({
  selector: 'app-do-menu',
  templateUrl: './do-menu.component.html',
  styleUrls: ['./do-menu.component.scss'],
})
export class DoMenuComponent implements OnInit {
  scenarioTabs: DoMenuScenarioTab[] = [];
  activeScenarioId: number | null = null;
  searchText = '';

  constructor(
    private dialogRef: MatDialogRef<DoMenuComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      scenarioBasedMenuItemsList?: unknown[];
    },
  ) {}

  ngOnInit(): void {
    this.scenarioTabs = this.normalizeScenarios(this.data?.scenarioBasedMenuItemsList);
    if (this.scenarioTabs.length) {
      this.activeScenarioId = this.scenarioTabs[0].scenarioId;
    }
  }

  get activeScenarioName(): string {
    return this.scenarioTabs.find((tab) => tab.scenarioId === this.activeScenarioId)?.scenarioName ?? '';
  }

  get activeScenarioMenuItems(): DoMenuItem[] {
    const tab = this.scenarioTabs.find((t) => t.scenarioId === this.activeScenarioId);
    return tab?.daysheetMenuItemsList ?? [];
  }

  get activeMenuItems(): DoMenuItem[] {
    const items = this.activeScenarioMenuItems;
    const query = this.searchText.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((item) => item.daysheetMenuItemName.toLowerCase().includes(query));
  }

  get activeScenarioSelectedCount(): number {
    return this.activeScenarioMenuItems.length;
  }

  get totalSelectedCount(): number {
    return this.scenarioTabs.reduce((sum, tab) => sum + tab.daysheetMenuItemsList.length, 0);
  }

  selectScenario(scenarioId: number): void {
    this.activeScenarioId = scenarioId;
    this.searchText = '';
  }

  close(): void {
    this.dialogRef.close();
  }

  private normalizeScenarios(rawList: unknown[] | undefined): DoMenuScenarioTab[] {
    if (!Array.isArray(rawList)) {
      return [];
    }
    return rawList
      .map((scenario: any) => {
        const scenarioId = Number(scenario?.scenarioId);
        const scenarioName = String(scenario?.scenarioName ?? '').trim();
        const daysheetMenuItemsList = this.normalizeMenuItems(scenario?.daysheetMenuItemsList);
        return {
          scenarioId,
          scenarioName,
          icon: this.getScenarioIcon(scenarioName),
          daysheetMenuItemsList,
        };
      })
      .filter((tab) => Number.isFinite(tab.scenarioId) && !!tab.scenarioName);
  }

  private normalizeMenuItems(rawList: unknown): DoMenuItem[] {
    if (!Array.isArray(rawList)) {
      return [];
    }
    return rawList
      .map((item: any) => ({
        daysheetMenuItemId: Number(item?.daysheetMenuItemId ?? item?.DaysheetMenuItemId),
        daysheetMenuItemName: String(
          item?.daysheetMenuItemName ?? item?.DaysheetMenuItemName ?? '',
        ).trim(),
      }))
      .filter((item) => Number.isFinite(item.daysheetMenuItemId) && !!item.daysheetMenuItemName);
  }

  private getScenarioIcon(scenarioName: string): string {
    const key = scenarioName.toLowerCase();
    if (key.includes('breakfast')) {
      return 'free_breakfast';
    }
    if (key.includes('lunch')) {
      return 'lunch_dining';
    }
    if (key.includes('snack')) {
      return 'bakery_dining';
    }
    if (key.includes('dinner')) {
      return 'dinner_dining';
    }
    return 'restaurant';
  }
}
